import 'amazon-connect-streams';
import { useEffect, useRef } from 'react';
import { useAppContext } from '../../../AppContext';
import { useTelephonyContext } from '../../../contexts/TelephonyContext';
import { Page } from '../../../models/Page';
import { addRecentCallsToLocalStorage } from '../../../utils/recentCallsUtil';
import { CloseContext } from '../../CassieContainer/websocket/types';

interface UseTelephonyContactHandlersProps {
  onViewContact?: (contactId: string | undefined, closeContext?: CloseContext) => void;
}

export function useTelephonyContactHandlers({ onViewContact }: UseTelephonyContactHandlersProps): void {
  const { setActivePage } = useAppContext();
  const {
    setAgentVoiceConnection,
    setIsTelephonyWidgetOpen,
    setTelephonyStatusBarOpen,
    setThirdPartyVoiceConnection,
    setVoiceContact
  } = useTelephonyContext();
  const isInitializedRef = useRef(false);

  useEffect(() => {
    if (isInitializedRef.current) {
      return;
    }
    isInitializedRef.current = true;

    connect.contact(contact => {
      const contactType = contact.getType();

      const isNotTelephonyContact =
        contactType !== connect.ContactType.VOICE && contactType !== connect.ContactType.QUEUE_CALLBACK;

      if (isNotTelephonyContact) {
        return;
      }

      setTelephonyStatusBarOpen(true);

      const handleIncomingCall = (): void => {
        setAgentVoiceConnection(contact.getInitialConnection());
        setIsTelephonyWidgetOpen(true);
        setActivePage(Page.TELEPHONY);
        addRecentCallsToLocalStorage(contact);
      };

      contact.onIncoming(() => {
        handleIncomingCall();
      });

      contact.onConnecting(() => {
        handleIncomingCall();
      });

      contact.onConnected(() => {
        setAgentVoiceConnection(contact.getInitialConnection());
      });

      contact.onRefresh(newContact => {
        setVoiceContact(newContact);
        setThirdPartyVoiceConnection(contact.getSingleActiveThirdPartyConnection() as connect.VoiceConnection);
      });

      contact.onDestroy(() => {
        setVoiceContact(null);
        setAgentVoiceConnection(null);
        setThirdPartyVoiceConnection(null);
        setTelephonyStatusBarOpen(false);
        if (onViewContact) onViewContact(undefined, { reason: 'call-ended', endedContactId: contact.contactId });
      });
    });
  }, [
    onViewContact,
    setActivePage,
    setAgentVoiceConnection,
    setIsTelephonyWidgetOpen,
    setTelephonyStatusBarOpen,
    setThirdPartyVoiceConnection,
    setVoiceContact
  ]);
}
